import { EntityIcon } from '@common/Entity/EntityIcons';
import { EntityNotFound } from '@common/EntityNotFound';
import { CreateGem } from '@common/EntityToCreate/CreateGem/CreateGem';
import CreateJob from '@common/EntityToCreate/CreateJob';
import CreatePipeline from '@common/EntityToCreate/CreatePipeline';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { ProjectFooterItems } from '@common/Footer/ProjectFooterItems';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { DeployDialog } from '@common/git/CommitFlow/DeployStep';
import { StepProvider } from '@common/git/CommitFlow/StepContext';
import { CommitsTab } from '@common/git/CommitFlow/Tab';
import { useBranchListQuery } from '@common/git/git-hooks';
import { useCheckGitTokenValidity } from '@common/git/GitCheckTokenValidity';
import { ProjectContextProvider, VersionContextProvider } from '@common/git/Project/context';
import { useIsProjectAdmin } from '@common/git/useIsProjectAdmin';
import { ProjectHeader } from '@common/Header/PageHeader';
import { sqlIdeJourney } from '@common/metrics/journey/journeys/sqlIde';
import { markOpenClick } from '@common/metrics/sessionLoad';
import { ProjectTabKeys } from '@common/project/constants';
import { QueryKeys } from '@common/queries/common';
import { projectDetailQuery, ProjectDetailQuery } from '@common/queries/project';
import { useIsTemplateProject } from '@common/template/hooks';
import { Entity } from '@common/types/Entity';
import { Private_Routes, getIDEUrl, getSqlIDEUrl } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { prefetchSqlPage } from '@pages/Sql/prefetch';
import {
  BackEndCodeLanguage,
  BackEndCodeLanguageType,
  ForkMode,
  ForkModeType,
  SyncMode,
  SyncModeType,
  VersionControlMode
} from '@prophecy/graphql-types/enums';
import { DotsHorizontalIcon } from '@prophecy/icons';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import {
  TabPane,
  Spinner,
  Centered,
  Stack,
  Text,
  Button,
  Dropdown,
  SlidingPanel,
  StackItem,
  MetadataPageContainer,
  Tooltip
} from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useGraphQlQuery } from 'frontend/core/src/data/util';
import { isModifiedClick } from 'frontend/core/src/layout/components';
import { getProviderType } from 'frontend/core/src/schema/util';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../../../context/mixpanel/context';
import { MixPanelContextKeys } from '../../../../context/mixpanel/types';
import { prefetchWhenIdle } from '../../../../utils/prefetchWhenIdle';
import { DeleteConfirmationDialog, getEntityDeletionMessage, useDeleteEntity } from '../../components/DeleteEntity';
import { ProjectRelationTab } from '../../components/RelationsTab';
import { ProjectSqlRelationsTab } from '../../components/SqlProjectRelationsTab';
import { CodeTab } from './CodeTab';
import { CheckForTemplateUpgradeFromProjectDetail } from './Components/CheckForTemplateUpgrade';
import ProjectName from './Components/ProjectName';
import { ProjectOperationForm } from './Components/ProjectOperation';
import { ShareWithTeam } from './Components/ShareWithTeam';
import { StyledTabs, TabRootCls, TeamLink } from './Components/styled';
import { VersionHistoryPage } from './Components/VersionHistoryPage';
import { DependenciesTab } from './DependenciesTab';
import ExternalGitConfig from './ExternalGitConfig';
import { ProjectInfo } from './InfoTab';
import { ReleasesTab } from './ReleasesTab';
import { Settings } from './Settings';

function useDeleteProject(projectId: string) {
  const [isDeleteDialogVisible, toggleDeleteDialog] = useState(false);
  const { remove, mutation } = useDeleteEntity({
    entityId: projectId,
    entity: Entity.Project,
    redirectURl: Private_Routes.User.tab.getUrl({ tab: Entity.Project.toLowerCase() + 's' }),
    showConfirmation: false
  });
  function onDelete() {
    remove();
  }
  return { isDeleteDialogVisible, toggleDeleteDialog, onDelete, mutation };
}

const useFetchProjectDetails = (projectId?: string, queriedBranch?: string) => {
  const { data, isLoading } = useGraphQlQuery(
    [QueryKeys.Git, QueryKeys.Project, 'project-detail', { projectId }],
    projectDetailQuery,
    undefined,
    {
      projectId: projectId as string,
      branch: queriedBranch
    }
  );

  return { projectDetails: data?.Project, isProjectLoading: isLoading };
};
export default function Page() {
  const { uid, tab: _tab } = useParams<ExtractUrlPathVariables<typeof Private_Routes.Project.tab.url>>();
  const tab = Object.values(ProjectTabKeys).find((item) => item === _tab) || ProjectTabKeys.About;
  const projectId = uid as string;
  const [searchParams] = useSearchParams();
  const queriedBranch = searchParams.get('branch') as string;
  const mode = searchParams.get('mode') as string;
  const templateId = searchParams.get('templateId') as string;
  let isReadMode = mode === 'readonly';

  const { projectDetails, isProjectLoading } = useFetchProjectDetails(projectId, queriedBranch);
  const isVerticalApp = useIsVerticalApp();

  const isSimpleVersionControl = projectDetails?.versionControlMode === VersionControlMode.Simple;
  if (isProjectLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!projectDetails) return <EntityNotFound uid={projectId} entity={Entity.Project} projectContext={false} />;
  return (
    <ProjectDetail
      isVerticalApp={isVerticalApp}
      tab={tab}
      templateId={templateId}
      isReadMode={isReadMode}
      projectId={projectId}
      queriedBranch={queriedBranch}
      projectDetails={projectDetails}
      isSimpleVersionControl={isSimpleVersionControl}
    />
  );
}
function ProjectDetail({
  isVerticalApp,
  tab,
  templateId,
  isReadMode,
  projectId,
  queriedBranch,
  projectDetails,
  isSimpleVersionControl
}: {
  isVerticalApp?: boolean;
  tab: string;
  templateId: string;
  isReadMode: boolean;
  projectId: string;
  queriedBranch: string;
  projectDetails: ProjectDetailQuery['Project'];
  isSimpleVersionControl: boolean;
}) {
  const { isProjectAdmin } = useIsProjectAdmin(projectId);
  const { isInitialLoading: isBranchListLoading, data: ProjectBranchData } = useBranchListQuery({
    projectId: projectId
  });

  const { track, addContext, removeContext } = useMixPanel();

  const isTemplateProject = useIsTemplateProject();

  useEffect(() => {
    addContext(MixPanelContextKeys.Project, {
      branch: ProjectBranchData?.Project.listBranches.workingBranch as string,
      id: projectId,
      name: projectDetails?.name as string
    });
    track(EntityEvents.common.metadata.entityOpen, {
      uid: projectId,
      name: projectDetails?.name,
      entity: Entity.Project,
      url: window.location.href
    });
    return () => {
      removeContext(MixPanelContextKeys.Project);
    };
  }, [
    ProjectBranchData?.Project.listBranches.workingBranch,
    addContext,
    track,
    removeContext,
    projectDetails?.name,
    projectId,
    queriedBranch
  ]);

  // Warm the SQL IDE chunk while the user is on project detail (and during branch-list wait),
  // so Open in Editor is not blocked on click → lazy SqlPage download. Idle-deferred, short
  // deadline: unlike the vertical-apps session-wide prefetch, this page's very likely next
  // action is clicking "Open in Editor" soon, so it can't afford to wait as long.
  useEffect(() => {
    if (projectDetails?.language !== BackEndCodeLanguage.sql) return;
    return prefetchWhenIdle(prefetchSqlPage);
  }, [projectDetails?.language]);

  if (isBranchListLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!projectDetails) return null;
  const {
    name,
    mainBranch,
    forkMode,
    team: { _id: teamId, name: teamName },
    latestAttemptedReleaseVersion,
    publishedToPackageHub,
    language,
    syncMode,
    providerType
  } = projectDetails;
  const workingBranch = ProjectBranchData?.Project.listBranches.workingBranch as string;

  const isSqlProject = language === BackEndCodeLanguage.sql;
  const isMainBranch = mainBranch === workingBranch;

  const header = (
    <ProjectHeader
      crumbs={[
        {
          children: 'Projects',
          icon: <EntityIcon size='m' entity={Entity.Project} />,
          elementType: 'link',
          to: Private_Routes.User.tab.getUrl({ tab: `${Entity.Project}s` }),
          'data-test-id': 'project-detail-projects-link-crumb'
        },
        {
          children: name,
          elementType: 'link',
          to: Private_Routes.Project.home.getUrl({ uid: projectId }),
          'data-test-id': 'project-detail-project-name-link-crumb'
        }
      ]}
    />
  );

  const isReadonly = projectDetails?.isExampleProject || isTemplateProject;
  const { isBigQuery, isSnowFlake } = getProviderType(providerType);
  const main = (
    <Stack gap={theme.spaces.x36} height='100%'>
      <Stack>
        <Stack direction='horizontal' align='space-between'>
          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x10} padding={theme.spaces.x10} grow='1'>
            <ProjectName uid={projectId} name={name} latestAttemptedReleaseVersion={latestAttemptedReleaseVersion} />
          </Stack>

          <Stack direction='horizontal' gap={theme.spaces.x10} shrink='0'>
            <Button
              variant='primary'
              elementType='link'
              to={
                isSqlProject ? getSqlIDEUrl({ uid: projectId }) : getIDEUrl({ entity: Entity.Lineage, uid: projectId })
              }
              onClick={(e) => {
                // A modified click opens a new tab instead of navigating this one — a pending mark
                // here would never be consumed and could misattribute to a later visit.
                if (isSqlProject && !isModifiedClick(e)) {
                  sqlIdeJourney.markPending('navigationStartAt', projectId);
                }
                markOpenClick({
                  entityType: isSqlProject ? 'sqlProject' : 'pipeline',
                  entityId: projectId,
                  entityName: name
                });
              }}
              data-test-id='project-detail-open-editor-button'>
              Open in Editor
            </Button>
            {!projectDetails?.isExampleProject && !isTemplateProject ? (
              <ProjectMenu
                isSqlProject={isSqlProject}
                isProjectAdmin={isProjectAdmin}
                latestAttemptedReleaseVersion={latestAttemptedReleaseVersion}
                syncMode={syncMode}
                forkMode={forkMode}
                projectId={projectId}
                name={name}
                teamName={teamName}
                teamId={teamId}
                isSimpleVersionControl={isSimpleVersionControl}
                isMainBranch={isMainBranch}
                branch={workingBranch}
                language={language}
              />
            ) : null}
          </Stack>
        </Stack>
        <Stack padding={`0 ${theme.spaces.x10}`}>
          <Text level='xs' tone={theme.colors.gray500}>
            by
            <TeamLink
              size='xs'
              elementType='link'
              variant='link'
              to={Private_Routes.Team.home.getUrl({ uid: teamId })}
              data-test-id='project-detail-team-link'>
              {teamName}
            </TeamLink>
          </Text>
        </Stack>
      </Stack>
      <StackItem grow='1'>
        <StyledTabs
          insideDialog={false}
          className={TabRootCls}
          activeKey={tab}
          onChange={(tab) => {
            track(EntityEvents.common.metadata.tabChange, {
              uid: projectId,
              tab,
              name,
              entity: Entity.Project,
              url: window.location.href
            });
          }}>
          <TabPane
            disabled={isReadMode}
            key={ProjectTabKeys.About}
            tab='About'
            elementType='link'
            to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.About })}
            data-test-id='project-detail-about-tab-link'>
            <VersionContextProvider projectId={projectId}>
              <ProjectInfo
                isMainBranch={isMainBranch}
                projectId={projectId}
                queriedBranch={queriedBranch}
                language={language}
                isSimpleVersionControl={isSimpleVersionControl}
              />
            </VersionContextProvider>
          </TabPane>

          <TabPane
            disabled={isReadMode}
            key={ProjectTabKeys.Content}
            tab='Content'
            elementType='link'
            to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Content })}
            data-test-id='project-detail-content-tab-link'>
            {language === BackEndCodeLanguage.sql ? (
              <ProjectSqlRelationsTab
                isVerticalApp={isVerticalApp}
                projectId={projectId}
                isSimpleVersionControl={isSimpleVersionControl}
                showStoredProcedures={isBigQuery || isSnowFlake}
              />
            ) : (
              <ProjectRelationTab projectId={projectId} />
            )}
          </TabPane>
          {!isSqlProject && !isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Dependencies}
              tab='Dependencies'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Dependencies })}>
              <DependenciesTab language={language} projectId={projectId} />
            </TabPane>
          ) : null}
          {isSimpleVersionControl && !isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.VersionHistory}
              tab='Version History'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.VersionHistory })}>
              <VersionContextProvider projectId={projectId}>
                <VersionHistoryPage />
              </VersionContextProvider>
            </TabPane>
          ) : null}
          {!isSimpleVersionControl && !isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Commits}
              tab='Commits'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Commits })}>
              <CommitsTab
                mainBranch={mainBranch}
                projectId={projectId}
                language={language}
                forkMode={forkMode}
                name={name}
                isSqlProject={isSqlProject}
                currentBranch={workingBranch}
              />
            </TabPane>
          ) : null}
          {!isSimpleVersionControl && !isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Releases}
              tab='Releases & Deployments'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Releases })}>
              <ReleasesTab showLogs projectId={projectId} language={language} />
            </TabPane>
          ) : null}
          {!isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Code}
              tab='Code'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Code })}>
              <CodeTab
                projectId={projectId}
                isSqlProject={isSqlProject}
                isSimpleVersionControl={isSimpleVersionControl}
                showStoredProcedures={isBigQuery || isSnowFlake}
              />
            </TabPane>
          ) : null}
          {!isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Access}
              tab='Access'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Access })}>
              <ShareWithTeam
                teamId={teamId}
                projectName={name}
                isSqlProject={isSqlProject}
                projectId={projectId}
                syncMode={syncMode}
                publishedToPackageHub={publishedToPackageHub ?? false}
                latestAttemptedReleaseVersion={latestAttemptedReleaseVersion}
              />
            </TabPane>
          ) : null}
          {!isReadonly ? (
            <TabPane
              disabled={isReadMode}
              key={ProjectTabKeys.Settings}
              tab='Settings'
              elementType='link'
              to={Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Settings })}>
              <Settings
                isReadMode={isReadMode || isMainBranch}
                isProjectAdmin={isProjectAdmin}
                syncMode={syncMode}
                templateId={templateId}
                uid={projectId}
                language={language}
                isSimpleVersionControl={isSimpleVersionControl}
                branch={workingBranch}
                queriedBranch={queriedBranch}
                isSqlProject={isSqlProject}
              />
            </TabPane>
          ) : null}
        </StyledTabs>
      </StackItem>
    </Stack>
  );
  const footer = isReadonly ? null : <ProjectFooterItems projectId={projectId} />;

  return (
    <ProjectContextProvider projectId={projectId}>
      <CodeGenerationContextProvider>
        <DocumentTitle title={`Project:${name || 'Summary'}`} />
        <MetadataPageContainer
          banner={<SnowflakePwdAuthBanner />}
          header={header}
          main={main}
          footer={footer}></MetadataPageContainer>

        {isMainBranch ? null : (
          <CheckForTemplateUpgradeFromProjectDetail projectId={projectId} queriedBranch={queriedBranch} />
        )}
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
}

function ProjectMenu({
  isSqlProject,
  isProjectAdmin,
  forkMode,
  projectId,
  name,
  teamName,
  syncMode,
  isMainBranch,
  language,
  branch,
  isSimpleVersionControl,
  teamId,
  latestAttemptedReleaseVersion
}: {
  isSqlProject: boolean;
  isProjectAdmin?: boolean;
  name: string;
  teamName: string;
  projectId: string;
  forkMode: ForkModeType;
  syncMode: SyncModeType;
  latestAttemptedReleaseVersion?: string;
  isMainBranch: boolean;
  language: BackEndCodeLanguageType;
  branch: string;
  isSimpleVersionControl?: boolean;
  teamId: string;
}) {
  const globalConf = useGlobalVariables();
  const [entityTypeToCreate, setEntityTypeToCreate] = useState<Entity | undefined>(undefined);
  const [isCloneDialogVisible, toggleCloneDialog] = useState(false);
  const [isSetupOnExternalGitDialogVisible, toggleSetupOnExternalGitDialog] = useState(false);
  const { isDeleteDialogVisible, toggleDeleteDialog, onDelete, mutation } = useDeleteProject(projectId);
  const [showExternalGitConfig, toggleExternalGitConfig] = useState(false);
  const [showDeployDialog, toggleDeployDialog] = useState(false);
  const { isLoading: isValidatingGit, checkGitTokenValidity } = useCheckGitTokenValidity();
  const hasReleaseTags = Boolean(latestAttemptedReleaseVersion);
  return (
    <>
      {entityTypeToCreate === Entity.Pipeline && (
        <CreatePipeline
          closePanel={() => setEntityTypeToCreate(undefined)}
          projectId={projectId}
          inProjectContext={true}
        />
      )}
      {entityTypeToCreate === Entity.Job && (
        <CreateJob closePanel={() => setEntityTypeToCreate(undefined)} projectId={projectId} inProjectContext={true} />
      )}
      {globalConf.isNewGembuilderEnabled && entityTypeToCreate === Entity.Gem && (
        <CreateGem
          language={language}
          branch={branch}
          closeDialog={() => setEntityTypeToCreate(undefined)}
          projectId={projectId}
        />
      )}
      {isDeleteDialogVisible && (
        <DeleteConfirmationDialog
          entity={Entity.Project}
          onOk={onDelete}
          onClose={() => toggleDeleteDialog(false)}
          entityName={name}
          message={getEntityDeletionMessage(Entity.Project, name, teamName)}
        />
      )}
      {isCloneDialogVisible && (
        <SlidingPanel
          size='s'
          open={isCloneDialogVisible}
          onClose={() => {
            toggleCloneDialog(false);
          }}
          footer={null}>
          <ProjectOperationForm
            isSqlProject={isSqlProject}
            isPackage={false}
            name={name}
            initialTeamUID={teamId}
            projectId={projectId}
            type='clone'
            toggle={toggleCloneDialog}
          />
        </SlidingPanel>
      )}
      {isSetupOnExternalGitDialogVisible && (
        <SlidingPanel
          size='s'
          open={isSetupOnExternalGitDialogVisible}
          onClose={() => toggleSetupOnExternalGitDialog(false)}
          footer={null}>
          <ProjectOperationForm
            isSqlProject={isSqlProject}
            isPackage={false}
            name={name}
            initialTeamUID={teamId}
            projectId={projectId}
            toggle={toggleSetupOnExternalGitDialog}
            type='setupOnExternalGit'
          />
        </SlidingPanel>
      )}
      {showExternalGitConfig && (
        <ExternalGitConfig
          projectId={projectId}
          syncMode={syncMode}
          showToggleButton={false}
          onClose={() => toggleExternalGitConfig(false)}
        />
      )}
      {showDeployDialog && (
        <StepProvider projectId={projectId}>
          <DeployDialog
            projectId={projectId}
            isSqlProject={isSqlProject}
            isProjectAdmin={isProjectAdmin}
            onClose={() => toggleDeployDialog(false)}
          />
        </StepProvider>
      )}
      <Dropdown
        data-test-id='project-detail-actions-dropdown'
        overlay={
          <>
            {isSqlProject ? null : (
              <>
                <Dropdown.Item
                  disabled={isMainBranch}
                  onClick={() => (isMainBranch ? null : setEntityTypeToCreate(Entity.Pipeline))}
                  data-test-id='add-pipeline-dropdown-item'>
                  Add Pipeline
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={isMainBranch}
                  onClick={() => (isMainBranch ? null : setEntityTypeToCreate(Entity.Job))}
                  data-test-id='add-job-dropdown-item'>
                  Add Job
                </Dropdown.Item>
                {globalConf.isNewGembuilderEnabled ? (
                  <Dropdown.Item
                    disabled={isMainBranch}
                    onClick={() => (isMainBranch ? null : setEntityTypeToCreate(Entity.Gem))}
                    data-test-id='add-gem-dropdown-item'>
                    Add Gem
                  </Dropdown.Item>
                ) : null}
              </>
            )}
            {globalConf.isReleaseV2Enabled &&
              (isSimpleVersionControl ? null : (
                <Tooltip
                  title={
                    hasReleaseTags ? undefined : 'No release tags. Go to the Commits tab to create a release first.'
                  }>
                  <span>
                    <Dropdown.Item
                      disabled={!hasReleaseTags}
                      onClick={() => toggleDeployDialog(true)}
                      data-test-id='deploy-dropdown-item'>
                      Deploy
                    </Dropdown.Item>
                  </span>
                </Tooltip>
              ))}
            <ExternalGitConfig projectId={projectId} syncMode={syncMode}>
              {(isExternal) => (
                <Dropdown.Item
                  onClick={() => (isExternal ? toggleExternalGitConfig(true) : null)}
                  disabled={!isExternal}
                  data-test-id='external-git-config-dropdown-item'>
                  External Git Config
                </Dropdown.Item>
              )}
            </ExternalGitConfig>

            {forkMode === ForkMode.Single ? (
              <Dropdown.Item onClick={() => toggleCloneDialog(true)} data-test-id='clone-dropdown-item'>
                Clone
              </Dropdown.Item>
            ) : null}
            {isSimpleVersionControl && isSqlProject && syncMode !== SyncMode.External && isProjectAdmin ? (
              <Dropdown.Item
                onClick={() => toggleSetupOnExternalGitDialog(true)}
                data-test-id='setup-external-git-dropdown-item'>
                Setup on external Git
              </Dropdown.Item>
            ) : null}
            <Dropdown.Item
              disabled={mutation.isLoading}
              onClick={() => toggleDeleteDialog(true)}
              data-test-id='delete-project-dropdown-item'>
              Delete
            </Dropdown.Item>
            {syncMode === SyncMode.External ? (
              <Dropdown.Item
                disabled={isValidatingGit}
                onClick={() => (isValidatingGit ? null : checkGitTokenValidity(projectId))}
                data-test-id='validate-git-token-dropdown-item'>
                Validate Git Token
              </Dropdown.Item>
            ) : null}
          </>
        }>
        <Button
          variant='secondaryGray'
          icon={<DotsHorizontalIcon type='default' />}
          aria-label='Project details menu'
          data-test-id='project-detail-menu-button'></Button>
      </Dropdown>
    </>
  );
}
